/**
 * Projections Page
 *
 * GM-facing page for entering annual projections.
 * Uses the SAME historical data API as the Historical Data page
 * to ensure data consistency.
 */

import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { useAuth } from '../auth/AuthContext';
import { canEditProjections, canUnlockProjections, canAdjustProjectionMonths } from '../auth/permissions';
import {
  ProjectionsControlPanel,
  ProjectionInputView,
} from '../components/projections';
import type { DepartmentAggregates } from '../components/projections';
import { DOCReportView } from '../components/doc-report';
import { ErrorAlert } from '../components/ErrorAlert';
import { getHistoricalData } from '../services/api';
import { createEmptyMonthlyTotals } from '../types';
import { HybridHistoricalData, convertToHybridData, getUnitCountsData } from '../utils/projectionHistoricalData';
import { getProjections, saveProjection, ProjectionDataWithViewData, SaveProjectionResponse } from '../services/projectionsApi';
import {
  calculateSeasonalityForProjection,
  HistoricalYearData,
} from '../utils/seasonalityCalculation';
import { useLocationParam } from '../hooks/useLocationParam';
import {
  getTargetProjectionYear,
  getDisplayHistoricalYears,
  getSeasonalityYears,
  isSeasonalityProvisional,
  getAvailableProjectionYears,
  getInputStateKey,
  isYearOpenForMonthlyAdjustment,
  ProjectionInput,
} from '../types/projections';
import { ProjectionViewData } from '../types/projectionViewData';
import { MonthlyTotals } from '../types';
import { applyDepartmentAggregates } from '../utils/projectionSaveAggregates';

export const Projections: React.FC = () => {
  const { isExecutive, userRole, assignedDepartmentIds } = useAuth();
  // Admin/Exec/GM can edit projections, DeptMgr is view-only
  const userCanEdit = canEditProjections(userRole);
  const userCanUnlock = canUnlockProjections(userRole);

  // Available years for dropdown (from first projection year to current target)
  const currentTargetYear = getTargetProjectionYear();
  const availableYears = getAvailableProjectionYears();

  // State
  const { locationId, setLocationId } = useLocationParam();
  const [selectedYear, setSelectedYear] = useState(currentTargetYear);
  const [isEditing, setIsEditing] = useState(false);

  // Display years (ascending) for tabs/columns: 4 during the Oct-Dec window (incl. the
  // partial prior year), else 3. Seasonality years (newest first) drive the 50/30/20
  // weights. Memoized to prevent infinite loops.
  const historicalYears = useMemo(() => getDisplayHistoricalYears(selectedYear), [selectedYear]);
  const seasonalityYears = useMemo(() => getSeasonalityYears(selectedYear), [selectedYear]);
  const seasonalityProvisional = isSeasonalityProvisional(selectedYear);
  // 'projection' or `historical${index}` (index into historicalYears)
  const [activeTab, setActiveTab] = useState<string>('projection');

  // Comparison mode: when enabled, shows actual data alongside projected data
  const [comparisonMode, setComparisonMode] = useState(false);
  const [targetYearActualData, setTargetYearActualData] = useState<HybridHistoricalData | null>(null);
  const [loadingActuals, setLoadingActuals] = useState(false);

  // Historical data state - SAME format as Historical Data page
  // Dynamic based on historicalYears
  const [historicalData, setHistoricalData] = useState<Record<number, HybridHistoricalData | null>>({});
  const [loading, setLoading] = useState(false);
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  // Executive monthly adjustments a GM save cleared because the annual changed
  const [droppedAdjustmentsNotice, setDroppedAdjustmentsNotice] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  // Projection data state (includes optional viewData from backend)
  const [projectionData, setProjectionData] = useState<ProjectionDataWithViewData | null>(null);

  // Local input state for unsaved changes
  const [localInputs, setLocalInputs] = useState<Record<string, { value: number; inputType: 'amount' | 'percentage'; isDirty: boolean }>>({});

  // Department aggregates from SeasonalityModeView - captures the EXACT displayed values
  // for TOTAL_EXPENSES and OPERATING_PROFIT so we save exactly what the user sees
  const [departmentAggregates, setDepartmentAggregates] = useState<DepartmentAggregates[]>([]);

  // Toast state for unsaved changes warning
  const [showUnsavedWarning, setShowUnsavedWarning] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState<(() => void) | null>(null);

  // Whether viewing current target year (can edit) vs archive (read-only)
  const isCurrentTargetYear = selectedYear === currentTargetYear;

  // Load historical data using the SAME API as Historical Data page
  const loadData = useCallback(async () => {
    setLoading(true);
    setLoadingProgress(0);
    setError(null);

    // Clear previous location's data to prevent stale data from showing
    setHistoricalData({});
    setProjectionData(null);
    setLocalInputs({});

    const isConsolidated = locationId === 0;

    try {
      // Build historical data record keyed by year
      const newHistoricalData: Record<number, HybridHistoricalData | null> = {};

      if (isConsolidated) {
        // For consolidated view, get historical data from the projections API response
        // The projections API aggregates historical data from all locations
        setLoadingProgress(25);
        const projResp = await getProjections({ locationId, targetYear: selectedYear, includeViewData: true });
        setLoadingProgress(100);

        if (projResp.success && projResp.data) {
          // Extract historicalRaw from projections response
          const historicalRaw = (projResp.data as any).historicalRaw;
          if (historicalRaw) {
            // Convert historicalRaw to HybridHistoricalData format
            historicalYears.forEach(year => {
              const yearKey = `year${year}`;
              const rawData = historicalRaw[yearKey];
              if (rawData) {
                newHistoricalData[year] = {
                  locationId: 0,
                  locationName: 'Consolidated',
                  year: rawData.year,
                  accounts: rawData.accounts || [],
                  docGross: rawData.docGross || [],
                  unitCounts: rawData.unitCounts || null,
                  locationOperatingProfit: rawData.locationOperatingProfit || null,
                } as HybridHistoricalData;
              } else {
                newHistoricalData[year] = null;
              }
            });
          }
          setHistoricalData(newHistoricalData);
          setProjectionData(projResp.data);
        } else {
          setHistoricalData(newHistoricalData);
          setProjectionData(null);
        }
      } else {
        // For regular locations, fetch historical data separately
        // Progress: one step per display year + the projections call
        const totalCalls = historicalYears.length + 1;
        let completedCalls = 0;
        const updateProgress = () => {
          completedCalls++;
          setLoadingProgress(Math.round((completedCalls / totalCalls) * 100));
        };

        // Create promises that update progress when they complete
        const historicalPromises = historicalYears.map(year =>
          getHistoricalData(locationId, year).then(result => {
            updateProgress();
            newHistoricalData[year] = result.success && result.data ? convertToHybridData(result.data) : null;
            return result;
          })
        );

        const projPromise = getProjections({ locationId, targetYear: selectedYear, includeViewData: true }).then(result => {
          updateProgress();
          return result;
        });

        // Wait for all to complete
        const [projResp] = await Promise.all([projPromise, Promise.all(historicalPromises)]);

        setHistoricalData(newHistoricalData);
        setProjectionData(projResp.success && projResp.data ? projResp.data : null);
      }
    } catch (err: any) {
      console.error('Error loading data:', err);
      setError(err.message || 'Failed to load data');
    } finally {
      setLoading(false);
      setLoadingProgress(0);
    }
  }, [locationId, selectedYear, historicalYears]);

  // Load data on mount and when location changes
  useEffect(() => {
    loadData();
  }, [loadData]);

  // Fetch target year actual data when comparison mode is enabled
  useEffect(() => {
    if (!comparisonMode) {
      setTargetYearActualData(null);
      return;
    }

    const fetchActualData = async () => {
      setLoadingActuals(true);
      try {
        const result = await getHistoricalData(locationId, selectedYear);
        if (result.success && result.data) {
          setTargetYearActualData(convertToHybridData(result.data));
        } else {
          setTargetYearActualData(null);
        }
      } catch (err) {
        console.error('Error loading actual data:', err);
        setTargetYearActualData(null);
      } finally {
        setLoadingActuals(false);
      }
    };

    fetchActualData();
  }, [comparisonMode, locationId, selectedYear]);

  // Get current historical data based on active tab (`historical${index}`)
  const getCurrentHistoricalData = () => {
    const match = activeTab.match(/^historical(\d+)$/);
    if (!match) return null;
    const year = historicalYears[Number(match[1])];
    return year !== undefined ? historicalData[year] || null : null;
  };

  /**
   * Helper to add two MonthlyTotals together (handles undefined)
   */
  const addMonthlyTotals = (a: MonthlyTotals | undefined, b: MonthlyTotals | undefined): MonthlyTotals => {
    const aVal = a || createEmptyMonthlyTotals();
    const bVal = b || createEmptyMonthlyTotals();
    return {
      month1: aVal.month1 + bVal.month1,
      month2: aVal.month2 + bVal.month2,
      month3: aVal.month3 + bVal.month3,
      month4: aVal.month4 + bVal.month4,
      month5: aVal.month5 + bVal.month5,
      month6: aVal.month6 + bVal.month6,
      month7: aVal.month7 + bVal.month7,
      month8: aVal.month8 + bVal.month8,
      month9: aVal.month9 + bVal.month9,
      month10: aVal.month10 + bVal.month10,
      month11: aVal.month11 + bVal.month11,
      month12: aVal.month12 + bVal.month12,
    };
  };

  /**
   * Get historical data for seasonality calculation for a given department.
   * Returns array of HistoricalYearData in order (most recent first).
   * Uses gross data from docGross for the specified department.
   * For vehicle departments, combines with F&I to get Total Gross (matching SeasonalityModeView logic).
   */
  const getHistoricalDataForSeasonality = useCallback((department: string): HistoricalYearData[] => {
    const result: HistoricalYearData[] = [];

    // Seasonality years, most recent first (weights are positional)
    for (const year of seasonalityYears) {
      const yearData = historicalData[year];
      if (yearData) {
        // Build a map of department gross data (matching SeasonalityModeView logic)
        const grossMap = new Map<string, MonthlyTotals>();

        // First, store all department gross values (check both 'gross' and 'monthlyGross')
        yearData.docGross.forEach(d => {
          const grossData = d.gross || d.monthlyGross;
          if (grossData) {
            grossMap.set(d.department, grossData);
          }
        });

        // For NEW_VEHICLE: combine with F_AND_I_NEW to get Total Gross
        const newVehicleGross = grossMap.get('NEW_VEHICLE');
        const fiNewGross = grossMap.get('F_AND_I_NEW');
        if (newVehicleGross) {
          grossMap.set('NEW_VEHICLE', addMonthlyTotals(newVehicleGross, fiNewGross));
        }

        // For USED_VEHICLE: combine with F_AND_I_USED to get Total Gross
        const usedVehicleGross = grossMap.get('USED_VEHICLE');
        const fiUsedGross = grossMap.get('F_AND_I_USED');
        if (usedVehicleGross) {
          grossMap.set('USED_VEHICLE', addMonthlyTotals(usedVehicleGross, fiUsedGross));
        }

        // Get the department's gross data
        const deptGross = grossMap.get(department);
        if (deptGross) {
          result.push({
            year,
            monthlyTotals: deptGross,
          });
        }
      }
    }

    return result;
  }, [historicalData, seasonalityYears]);

  /**
   * Get historical UNIT data for department-level seasonality calculation.
   * This is used for the "Total Units" row - NOT brand-specific units.
   * Uses monthlyNewUnits/monthlyUsedUnits from unitCounts data.
   */
  const getHistoricalUnitDataForDepartment = useCallback((department: 'NEW_VEHICLE' | 'USED_VEHICLE'): HistoricalYearData[] => {
    const result: HistoricalYearData[] = [];

    // Seasonality years, most recent first (weights are positional)
    for (const year of seasonalityYears) {
      const yearData = historicalData[year];
      if (yearData) {
        const unitData = getUnitCountsData(yearData);
        const monthlyUnits = department === 'NEW_VEHICLE'
          ? unitData?.monthlyNewUnits
          : unitData?.monthlyUsedUnits;

        if (monthlyUnits) {
          result.push({
            year,
            monthlyTotals: monthlyUnits,
          });
        }
      }
    }

    return result;
  }, [historicalData, seasonalityYears]);

  /**
   * Get historical data for a specific account (e.g., 019A for Advertising).
   * This is used for expense and income accounts to get account-specific seasonality.
   * Matches the logic in SeasonalityModeView's getAccountSeasonalityWeights.
   */
  const getHistoricalDataForAccount = useCallback((accountCode: string): HistoricalYearData[] => {
    const result: HistoricalYearData[] = [];

    // Seasonality years, most recent first (weights are positional)
    for (const year of seasonalityYears) {
      const yearData = historicalData[year];
      if (yearData?.accounts) {
        // Find the account in this year's data
        const account = yearData.accounts.find(acc => acc.account === accountCode);
        if (account?.monthlyTotals) {
          result.push({
            year,
            monthlyTotals: account.monthlyTotals,
          });
        }
      }
    }

    return result;
  }, [historicalData, seasonalityYears]);

  // Check for unsaved changes
  const hasUnsavedChanges = Object.values(localInputs).some(input => input.isDirty);

  // Handle location change with unsaved changes check
  const handleLocationChange = useCallback((newLocationId: number) => {
    if (hasUnsavedChanges) {
      setPendingNavigation(() => () => {
        setLocationId(newLocationId);
        setIsEditing(false);
        setLocalInputs({});
      });
      setShowUnsavedWarning(true);
      return;
    }
    setLocationId(newLocationId);
  }, [hasUnsavedChanges]);

  // Handle year change with unsaved changes check
  const handleYearChange = useCallback((newYear: number) => {
    if (hasUnsavedChanges) {
      setPendingNavigation(() => () => {
        setSelectedYear(newYear);
        setIsEditing(false);
        setLocalInputs({});
        setActiveTab('projection');
      });
      setShowUnsavedWarning(true);
      return;
    }
    setSelectedYear(newYear);
    setActiveTab('projection');
  }, [hasUnsavedChanges]);

  // Handle start edit - simply enable editing mode
  const handleStartEdit = useCallback(() => {
    setIsEditing(true);
  }, []);

  // Handle cancel - reset to saved values
  const handleCancel = useCallback(() => {
    if (hasUnsavedChanges) {
      setPendingNavigation(() => () => {
        setIsEditing(false);
        // Reset local inputs to API values
        const resetInputs: Record<string, { value: number; inputType: 'amount' | 'percentage'; isDirty: boolean }> = {};
        if (projectionData?.inputs) {
          for (const input of projectionData.inputs) {
            const key = getInputStateKey(input.department, input.metric);
            resetInputs[key] = {
              value: input.value,
              inputType: input.inputType,
              isDirty: false,
            };
          }
        }
        setLocalInputs(resetInputs);
      });
      setShowUnsavedWarning(true);
      return;
    }
    setIsEditing(false);
  }, [hasUnsavedChanges, projectionData?.inputs]);

  // Handle discard changes from warning toast
  const handleDiscardChanges = useCallback(() => {
    setShowUnsavedWarning(false);
    if (pendingNavigation) {
      pendingNavigation();
      setPendingNavigation(null);
    }
  }, [pendingNavigation]);

  // Handle input value change
  const handleValueChange = useCallback((
    department: string,
    metric: string,
    value: number,
    inputType?: 'amount' | 'percentage'
  ) => {
    const key = getInputStateKey(department, metric);
    const defaultInputType = ['VARIABLE_EXPENSES', 'SEMI_FIXED_EXPENSES', 'SALES_EXPENSES', 'FIXED_EXPENSES', 'OTHER_EXPENSES', 'SELLING_EXPENSE', 'FIXED_EXPENSE'].includes(metric) ? 'percentage' : 'amount';

    setLocalInputs(prev => ({
      ...prev,
      [key]: {
        value,
        inputType: inputType || prev[key]?.inputType || defaultInputType,
        isDirty: true,
      },
    }));
  }, []);

  // Internal save function. Returns true only when the save succeeded (or there was
  // nothing to save), so callers can avoid navigating away from unsaved edits.
  const handleSaveInternal = useCallback(async (): Promise<boolean> => {
    const hasChanges = Object.values(localInputs).some(input => input.isDirty);
    const hasExistingInputs = projectionData?.inputs && projectionData.inputs.length > 0;

    // If no changes AND no existing inputs to recalculate, nothing to save
    if (!hasChanges && !hasExistingInputs) {
      setIsEditing(false);
      return true;
    }

    // The department totals (TOTAL_EXPENSES, OPERATING_PROFIT, UNITS...) come from the
    // (always-mounted) seasonality view; never save without them
    if (departmentAggregates.length === 0) {
      setError('Projection totals are still being calculated - please try saving again in a moment.');
      return false;
    }

    setSaving(true);

    try {
      // Build list of inputs to save, tracking which ones are changed
      const inputsToSave: (ProjectionInput & { isChanged?: boolean })[] = [];

      // Build a set of changed input keys for quick lookup
      const changedInputKeys = new Set<string>();

      // Metrics that are CALCULATED, not user inputs - filter these out
      const calculatedMetrics = ['TOTAL_EXPENSES', 'OPERATING_PROFIT'];

      // Include existing inputs (updated or not), but skip calculated metrics
      if (projectionData?.inputs) {
        for (const existingInput of projectionData.inputs) {
          // Skip calculated metrics - these will be recalculated below
          if (calculatedMetrics.includes(existingInput.metric)) {
            console.log(`Filtering out calculated metric from inputs: ${existingInput.department}#${existingInput.metric}`);
            continue;
          }

          const key = getInputStateKey(existingInput.department, existingInput.metric);
          const localInput = localInputs[key];

          if (localInput?.isDirty) {
            changedInputKeys.add(key);
            inputsToSave.push({
              department: existingInput.department,
              metric: existingInput.metric,
              inputType: localInput.inputType,
              value: localInput.value,
              isChanged: true,
            });
          } else {
            inputsToSave.push({
              department: existingInput.department,
              metric: existingInput.metric,
              inputType: existingInput.inputType,
              value: existingInput.value,
              isChanged: false,
            });
          }
        }
      }

      // Add any new inputs that don't exist in API data
      for (const [key, localInput] of Object.entries(localInputs)) {
        if (localInput.isDirty) {
          const [department, metric] = key.split('#');
          const existsInApi = projectionData?.inputs?.some(
            i => i.department === department && i.metric === metric
          );

          if (!existsInApi) {
            changedInputKeys.add(key);
            inputsToSave.push({
              department,
              metric,
              inputType: localInput.inputType,
              value: localInput.value,
              isChanged: true,
            });
          }
        }
      }

      // IMPORTANT: Recalculate GROSS and TOTAL_GROSS for vehicle departments that use per-unit inputs
      // This ensures these derived values stay in sync when GROSS_PER_UNIT, FI_INCOME_PER_UNIT, or UNITS change
      for (const dept of ['NEW_VEHICLE', 'USED_VEHICLE'] as const) {
        // Get per-unit values from inputsToSave
        const grossPerUnitInput = inputsToSave.find(i => i.department === dept && i.metric === 'GROSS_PER_UNIT');
        const fiPerUnitInput = inputsToSave.find(i => i.department === dept && i.metric === 'FI_INCOME_PER_UNIT');

        // Get all brand-specific unit inputs
        const unitInputs = inputsToSave.filter(
          i => i.department === dept && i.metric.startsWith('UNITS_') && !i.metric.includes('PER_UNIT')
        );

        // Only recalculate if this is a per-unit location (a per-unit input and units).
        // Either per-unit input is enough: F&I-only entries must still derive FI_INCOME.
        if ((grossPerUnitInput || fiPerUnitInput) && unitInputs.length > 0) {
          const grossPerUnit = grossPerUnitInput?.value || 0;
          const fiPerUnit = fiPerUnitInput?.value || 0;
          const totalUnits = unitInputs.reduce((sum, i) => sum + i.value, 0);

          // Calculate derived values
          const calculatedGross = grossPerUnit * totalUnits;
          const calculatedTotalGross = (grossPerUnit + fiPerUnit) * totalUnits;

          // Check if any inputs affecting GROSS/TOTAL_GROSS have changed
          const derivedValuesChanged = !!grossPerUnitInput?.isChanged ||
            fiPerUnitInput?.isChanged ||
            unitInputs.some(i => i.isChanged);

          console.log(`Recalculating ${dept}: ${grossPerUnit}/unit + ${fiPerUnit}/unit × ${totalUnits} units = GROSS ${calculatedGross}, TOTAL_GROSS ${calculatedTotalGross}${derivedValuesChanged ? ' (CHANGED)' : ' (unchanged)'}`);

          // Update or add GROSS input
          const existingGrossIndex = inputsToSave.findIndex(i => i.department === dept && i.metric === 'GROSS');
          if (existingGrossIndex >= 0) {
            inputsToSave[existingGrossIndex].value = calculatedGross;
            inputsToSave[existingGrossIndex].isChanged = derivedValuesChanged;
          } else {
            inputsToSave.push({
              department: dept,
              metric: 'GROSS',
              inputType: 'amount',
              value: calculatedGross,
              isChanged: derivedValuesChanged,
            });
          }

          // Update or add FI_INCOME (F&I = FI_INCOME_PER_UNIT x units). Consolidation reads
          // GROSS as front-only and FI_INCOME as the separate F&I amount.
          const calculatedFiIncome = fiPerUnit * totalUnits;
          const existingFiIndex = inputsToSave.findIndex(i => i.department === dept && i.metric === 'FI_INCOME');
          if (existingFiIndex >= 0) {
            inputsToSave[existingFiIndex].value = calculatedFiIncome;
            inputsToSave[existingFiIndex].isChanged = derivedValuesChanged;
          } else {
            inputsToSave.push({
              department: dept,
              metric: 'FI_INCOME',
              inputType: 'amount',
              value: calculatedFiIncome,
              isChanged: derivedValuesChanged,
            });
          }

          // Update or add TOTAL_GROSS input
          const existingTotalGrossIndex = inputsToSave.findIndex(i => i.department === dept && i.metric === 'TOTAL_GROSS');
          if (existingTotalGrossIndex >= 0) {
            inputsToSave[existingTotalGrossIndex].value = calculatedTotalGross;
            inputsToSave[existingTotalGrossIndex].isChanged = derivedValuesChanged;
          } else {
            inputsToSave.push({
              department: dept,
              metric: 'TOTAL_GROSS',
              inputType: 'amount',
              value: calculatedTotalGross,
            });
          }
        }
      }

      // Build a map of expense account dollar amounts from departmentAggregates
      // These are the EXACT values displayed on screen - use them directly
      const expenseAccountAmounts = new Map<string, { annual: number; monthly: MonthlyTotals }>();
      for (const agg of departmentAggregates) {
        if (agg.expenseAccounts) {
          for (const [accCode, amounts] of Object.entries(agg.expenseAccounts)) {
            const key = `${agg.department}#EXPENSE_${accCode}`;
            expenseAccountAmounts.set(key, amounts);
          }
        }
      }

      // Calculate seasonality for each input using frontend logic
      // This ensures the Forecast page purple line matches what's shown on the Projections page
      // Always recalculate to ensure backend matches frontend display (which uses corrected year-order algorithm)
      const inputsWithSeasonality: ProjectionInput[] = inputsToSave.map(input => {
        // For EXPENSE_ metrics, use the pre-calculated dollar amounts from departmentAggregates
        // These are the EXACT values displayed on the Projections page
        if (input.metric.startsWith('EXPENSE_')) {
          const key = `${input.department}#${input.metric}`;
          const expenseData = expenseAccountAmounts.get(key);

          if (expenseData) {
            // Calculate seasonality weights from the monthly distribution
            const weights = createEmptyMonthlyTotals();
            if (expenseData.annual !== 0) {
              for (let m = 1; m <= 12; m++) {
                const monthKey = `month${m}` as keyof MonthlyTotals;
                weights[monthKey] = expenseData.monthly[monthKey] / expenseData.annual;
              }
            }

            return {
              department: input.department,
              metric: input.metric,
              inputType: input.inputType,
              value: input.value,
              // Send the actual dollar amounts, not percentages
              monthlyDistribution: expenseData.monthly,
              seasonalityWeights: weights,
              yearsUsedForWeights: [...seasonalityYears],
            };
          }
        }

        // For non-expense inputs, use the original seasonality calculation
        let histData: HistoricalYearData[];

        // For INCOME_ metrics, use account-specific historical data
        if (input.metric.startsWith('INCOME_')) {
          const accountCode = input.metric.replace(/^INCOME_/, '');
          histData = getHistoricalDataForAccount(accountCode);

          if (histData.length === 0) {
            histData = getHistoricalDataForSeasonality(input.department);
          }
        } else {
          // For all other metrics, use department-level gross seasonality
          histData = getHistoricalDataForSeasonality(input.department);
        }

        const annualValue = input.value;
        const seasonality = calculateSeasonalityForProjection(annualValue, histData);

        return {
          department: input.department,
          metric: input.metric,
          inputType: input.inputType,
          value: input.value,
          monthlyDistribution: seasonality.monthlyDistribution,
          seasonalityWeights: seasonality.seasonalityWeights,
          yearsUsedForWeights: seasonality.yearsUsed,
        };
      });

      // Upsert the department aggregates (UNITS, TOTAL_EXPENSES, OPERATING_PROFIT, gross,
      // OTHERS income) exactly as displayed - zeros included
      applyDepartmentAggregates(inputsWithSeasonality, departmentAggregates, seasonalityYears);

      const response = await saveProjection({
        locationId,
        targetYear: selectedYear,
        inputs: inputsWithSeasonality,
      });

      if (response.success) {
        // The save Lambda returns the SaveProjectionResponse body flat (no `data` wrapper)
        const dropped = (response as unknown as SaveProjectionResponse).droppedAdjustments || [];
        setDroppedAdjustmentsNotice(dropped.length === 0 ? null :
          `Executive month adjustments were cleared by this save: ${dropped
            .map(d => `${d.department.replace(/_/g, ' ').toLowerCase()} ${d.metric.replace(/_/g, ' ').toLowerCase()} (by ${d.adjustedByName}: ${d.reason})`)
            .join('; ')}. They can be re-entered from the Monthly Seasonality view.`);
        // Clear dirty flags and exit edit mode
        setLocalInputs({});
        setIsEditing(false);
        // Reload data to get updated calculations
        await loadData();
        return true;
      }
      setError(response.error || 'Failed to save projections');
      return false;
    } catch (err: any) {
      console.error('Error saving projections:', err);
      setError(err.message || 'Failed to save projections');
      return false;
    } finally {
      setSaving(false);
    }
  }, [localInputs, projectionData?.inputs, locationId, selectedYear, loadData, getHistoricalDataForSeasonality, getHistoricalDataForAccount, departmentAggregates, seasonalityYears]);

  // Handle save button click
  const handleSave = useCallback(async () => {
    await handleSaveInternal();
  }, [handleSaveInternal]);

  // Handle save from warning toast - only navigate if the save succeeded,
  // otherwise the reload would discard the user's unsaved edits
  const handleSaveFromWarning = useCallback(async () => {
    setShowUnsavedWarning(false);
    const saved = await handleSaveInternal();
    if (saved && pendingNavigation) {
      pendingNavigation();
      setPendingNavigation(null);
    }
  }, [pendingNavigation, handleSaveInternal]);

  // Browser navigation warning for unsaved changes
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedChanges]);

  const currentData = getCurrentHistoricalData();
  // Get locationName from any available historical data
  const locationName = historicalYears.map(y => historicalData[y]?.locationName).find(Boolean) || '';
  const status = projectionData?.status || 'draft';

  // Check if this is the consolidated view (locationId=0)
  const isConsolidated = locationId === 0;

  // canEdit: role permission AND the backend's year-scoped canEdit (window open for this
  // year, or an Admin/CEO/Executive 24h override - incl. an archive unlock).
  // Consolidated view is always read-only
  const canEditCurrentYear = !isConsolidated && userCanEdit && isCurrentTargetYear && (projectionData?.canEdit ?? true);
  const isArchiveUnlocked = !isCurrentTargetYear && projectionData?.canEdit === true;
  const canEditArchive = !isConsolidated && userCanEdit && isArchiveUnlocked;
  const canEdit = canEditCurrentYear || canEditArchive;

  // After an archive unlock, reload so the backend's canEdit reflects the new override
  const handleArchiveUnlocked = () => {
    loadData();
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-full mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Page Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">
            Projections
          </h1>
          <p className="mt-2 text-gray-600">
            {locationName ? `${locationName} - ` : ''}
            View historical data and enter annual targets
          </p>
        </div>

        {/* Control Panel */}
        <ProjectionsControlPanel
          locationId={locationId}
          targetYear={selectedYear}
          status={status}
          metadata={projectionData?.metadata || null}
          onLocationChange={handleLocationChange}
          availableYears={availableYears}
          currentTargetYear={currentTargetYear}
          onYearChange={handleYearChange}
          isEditing={isEditing}
          hasUnsavedChanges={hasUnsavedChanges}
          canEdit={canEdit}
          onStartEdit={handleStartEdit}
          onSave={handleSave}
          onCancel={handleCancel}
          onRefresh={loadData}
          isExecutive={userCanUnlock}
          saving={saving}
          loading={loading}
          editingWindowStatus={projectionData?.editingWindowStatus}
          isCurrentTargetYear={isCurrentTargetYear}
          isArchiveUnlocked={isArchiveUnlocked}
          onArchiveUnlocked={handleArchiveUnlocked}
          isConsolidated={isConsolidated}
        />

        {/* Unsaved Changes Warning Toast */}
        {showUnsavedWarning && (
          <div className="fixed bottom-4 right-4 z-50 bg-white rounded-lg shadow-lg border border-yellow-200 p-4 max-w-md">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <svg className="h-6 w-6 text-yellow-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <div className="ml-3 flex-1">
                <h3 className="text-sm font-medium text-gray-900">Unsaved Changes</h3>
                <p className="mt-1 text-sm text-gray-500">
                  You have unsaved changes. Would you like to save them before continuing?
                </p>
                <div className="mt-3 flex space-x-3">
                  <button
                    onClick={handleSaveFromWarning}
                    className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    Save Changes
                  </button>
                  <button
                    onClick={handleDiscardChanges}
                    className="inline-flex items-center px-3 py-1.5 border border-gray-300 text-xs font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    Discard
                  </button>
                  <button
                    onClick={() => setShowUnsavedWarning(false)}
                    className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-gray-500 hover:text-gray-700"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Error Alert */}
        {error && <ErrorAlert message={error} />}
        {droppedAdjustmentsNotice && (
          <div className="bg-amber-50 border border-amber-200 rounded-md p-4 mb-4 flex items-start" role="status">
            <p className="flex-1 text-sm text-amber-800">{droppedAdjustmentsNotice}</p>
            <button onClick={() => setDroppedAdjustmentsNotice(null)} className="ml-4 text-amber-500 hover:text-amber-700" aria-label="Dismiss">
              <span className="text-xl">&times;</span>
            </button>
          </div>
        )}

        {/* Loading State - Progress bar with percentage */}
        {loading && (
          <div className="py-12">
            <div className="w-full max-w-md mx-auto">
              <div className="relative pt-1">
                {/* Status label with percentage */}
                <div className="flex mb-2 items-center justify-between">
                  <span className="text-xs font-semibold inline-block text-blue-600">
                    Loading Data
                  </span>
                  <span className="text-xs font-semibold inline-block text-blue-600">
                    {loadingProgress}%
                  </span>
                </div>

                {/* Progress bar with smooth transition */}
                <div className="overflow-hidden h-3 mb-2 text-xs flex rounded-full bg-gray-200">
                  <div
                    className="bg-blue-500 h-full rounded-full transition-all duration-300 ease-out"
                    style={{ width: `${loadingProgress}%` }}
                  />
                </div>

                {/* Status message */}
                <div className="text-center">
                  <p className="text-sm text-gray-600">
                    {loadingProgress < 100
                      ? 'Fetching projection and historical data...'
                      : 'Complete!'}
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Year Tab Navigation */}
        {!loading && (
          <div className="mb-6">
            <div className="border-b border-gray-200">
              <nav className="-mb-px flex space-x-8">
                {historicalYears.map((year, index) => {
                  const tab = `historical${index}`;
                  return (
                    <button
                      key={tab}
                      onClick={() => setActiveTab(tab)}
                      className={`py-2 px-1 border-b-2 font-medium text-sm ${
                        activeTab === tab
                          ? 'border-blue-500 text-blue-600'
                          : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                      }`}
                    >
                      {year} Historical
                    </button>
                  );
                })}
                <button
                  onClick={() => setActiveTab('projection')}
                  className={`py-2 px-1 border-b-2 font-medium text-sm ${
                    activeTab === 'projection'
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  {selectedYear} Projection
                </button>
              </nav>
            </div>
          </div>
        )}

        {/* Main Content - Use the SAME DOCReportView component as Historical Data page */}
        {!loading && activeTab !== 'projection' && currentData && (
          <DOCReportView
            locationName={currentData.locationName}
            locationId={currentData.locationId}
            year={currentData.year}
            accounts={currentData.accounts}
            docGross={currentData.docGross}
            unitCountsData={getUnitCountsData(currentData)}
            formatCurrency={(value) => {
              if (value === 0) return '--';
              return new Intl.NumberFormat('en-US', {
                style: 'currency',
                currency: 'USD',
                minimumFractionDigits: 0,
                maximumFractionDigits: 0,
              }).format(value);
            }}
            assignedDepartmentIds={assignedDepartmentIds}
          />
        )}

        {/* Projection Tab */}
        {!loading && activeTab === 'projection' && (
          <>
            {/* Comparison Mode Toggle */}
            <div className="mb-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    className="sr-only peer"
                    checked={comparisonMode}
                    onChange={(e) => setComparisonMode(e.target.checked)}
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                  <span className="ml-3 text-sm font-medium text-gray-700">
                    Show Actuals
                  </span>
                </label>
                {comparisonMode && (
                  <span className="text-xs text-gray-500">
                    {loadingActuals ? (
                      <span className="flex items-center gap-1">
                        <svg className="animate-spin h-3 w-3" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                        </svg>
                        Loading actuals...
                      </span>
                    ) : targetYearActualData ? (
                      <span className="text-green-600">Actual data loaded</span>
                    ) : (
                      <span className="text-amber-600">No actual data for {selectedYear}</span>
                    )}
                  </span>
                )}
              </div>
            </div>

            {/* Projection Input View */}
            {(
          <ProjectionInputView
            locationName={locationName}
            locationId={locationId}
            targetYear={selectedYear}
            historicalYears={historicalYears}
            seasonalityYears={seasonalityYears}
            seasonalityProvisional={seasonalityProvisional}
            historicalDataByYear={Object.fromEntries(historicalYears.map(year => {
              const data = historicalData[year];
              return [year, data ? {
                year: data.year,
                accounts: data.accounts,
                docGross: data.docGross,
                unitCountsData: getUnitCountsData(data),
                locationOperatingProfit: data.locationOperatingProfit,
              } : null];
            }))}
            inputs={projectionData?.inputs || []}
            calculations={projectionData?.calculations || []}
            localInputs={localInputs}
            isEditing={isEditing}
            onValueChange={handleValueChange}
            formatCurrency={(value) => {
              if (value === 0) return '--';
              return new Intl.NumberFormat('en-US', {
                style: 'currency',
                currency: 'USD',
                minimumFractionDigits: 0,
                maximumFractionDigits: 0,
              }).format(value);
            }}
            onAggregatesCalculated={setDepartmentAggregates}
            comparisonMode={comparisonMode}
            actualData={targetYearActualData ? {
              year: targetYearActualData.year,
              accounts: targetYearActualData.accounts,
              docGross: targetYearActualData.docGross,
              unitCountsData: getUnitCountsData(targetYearActualData),
              locationOperatingProfit: targetYearActualData.locationOperatingProfit,
            } : null}
            isConsolidated={isConsolidated}
            expensesByAccount={(projectionData as any)?.expensesByAccount}
            viewData={projectionData?.viewData}
            assignedDepartmentIds={assignedDepartmentIds}
            monthlyAdjustment={{
              // Hidden while annual edits are unsaved: a monthly save reloads the projection,
              // which would discard them (and the adjustment would sit on the old annual)
              canAdjust: canAdjustProjectionMonths(userRole) && !isConsolidated && !hasUnsavedChanges
                && isYearOpenForMonthlyAdjustment(selectedYear, projectionData?.metadata ?? null),
              locationId,
              targetYear: selectedYear,
              onSaved: loadData,
            }}
          />
            )}
          </>
        )}

        {/* No data message */}
        {!loading && activeTab !== 'projection' && !currentData && (
          <div className="bg-white rounded-lg shadow p-6">
            <p className="text-gray-500">
              No historical data available for this year.
              Please load data from the Historical Data page first.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};
